import React, { useContext, useState } from "react";
import { GetStaticProps } from "next";
import Router from "next/router";
import { NextSeo } from "next-seo";
import moment from "moment-timezone";

import styles from "src/styles/game.scss";
import MatchupTeamsPlayerLineup from "src/components/Matchup/MatchupTeamsPlayerLineup";
import MatchupTeamsPlayerInjuries from "src/components/Matchup/MatchupTeamsPlayerInjuries";
import SchedulePanel from "src/components/Schedule";
import Error from "src/pages/_error";
import { GameDetailHeader, GameAnalysis, GameDetailStartingPitchers, BestBets } from "src/components/GameDetail";
import GameLines from "src/components/GameLines";
import TrendsOverView from "src/components/GameDetail/TrendsOverView";
import TeamSummary from "src/components/GameDetail/TeamSummary";
import { EVENT_STATIC_QUERY, ALL_EVENTS_SLUGS_QUERY } from "src/gql/queries";
import { useGame } from "src/services/EventService";
import { capitalizeWords } from "src/services/DisplayService";
import { rfc3986EncodeURIComponent } from "src/services/UtilService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import LiveGameBar from "src/components/LiveGameBar";
import Chart from "src/components/Chart";
import { DEFAULT_TIMEZONE, IS_CLIENT, SLUG_SPORTS_MAP, SPORTS_MAP, WEB_SPORTS_MAP } from "src/constants";
import OffersCarousel from "src/components/OffersCarousel";
import RecommendedBets from "src/components/RecommendedBets";
import PopularEvents from "src/components/PopularEvents";
import EmailCapture from "src/components/EmailCapture";
import Spacer from "src/components/Spacer";
import { PlayerPropsPanel } from "src/components/PlayerProps";
import { initializeApollo } from "src/lib/apollo-client";
import { NewsArticleSchema } from "src/services/SchemaService";
import { currentSport } from "src/services/ApolloService";
import { EventSlugIdAndLeague } from "src/typings/events";
import { getStringQueryParam } from "src/services/UrlService";

// Chop up the URL slug and turn it into a page title.
const getPageTitle = (slugId: string) => {
  if (slugId) {
    const slugPieces = slugId.split("-");
    slugPieces[slugPieces.length - 1] = slugPieces[slugPieces.length - 1].split("_")[0];
    let pageTitle = "",
      midString = "";
    for (let i = 0; i < slugPieces.length - 3; i++) {
      midString += slugPieces[i] + " ";
    }
    pageTitle += capitalizeWords(midString);
    pageTitle += "Prediction ";
    pageTitle += `${slugPieces[slugPieces.length - 3]}/${slugPieces[slugPieces.length - 2]}/${
      slugPieces[slugPieces.length - 1]
    }`;
    return pageTitle + " | BetQL";
  }
  return "Game Prediction | BetQL";
};

interface GamesDetailViewProps {
  staticEvent: any;
  prismicGame: any;
  sportKey: string;
  slugId: string;
}

const Game = ({ staticEvent, prismicGame, sportKey, slugId }: GamesDetailViewProps) => {
  const [lineFilter] = useState("combined");
  const ctx = useContext(SubscriptionContext);
  const currentSportObj = WEB_SPORTS_MAP[sportKey];

  const { game: fullGame, response: eventResponse } = useGame({ slug: slugId });
  const event = fullGame || staticEvent;

  // 404
  if (!eventResponse?.loading && event) {
    // League doesn't match
    const leagueName = event?.league?.name;
    if (!currentSportObj || currentSportObj?.key !== leagueName) {
      if (IS_CLIENT) {
        // on the client redirect to the 404 page
        Router.push("/404");
        return null;
      } else {
        // on the server render the Error page with a 404
        return <Error statusCode={404} />;
      }
    }
    // Game doesn't exist
  } else if (!staticEvent || (!eventResponse?.loading && !event)) {
    if (IS_CLIENT) {
      // on the client redirect to the 404 page
      Router.push("/404");
      return null;
    } else {
      // on the server render the Error page with a 404
      return <Error statusCode={404} />;
    }
  }
  // March, 17, 2021
  // At Brian Hunt's request, we are going to status 404 all game detail pages before Jan 1st, 2021
  // This will get rid of the old pages that are no longer relevant in the google index so
  // google will stop thinking we are spamming them with irrelevant pages
  if (
    currentSportObj?.key !== "NFL" &&
    moment.tz("2021-01-01", DEFAULT_TIMEZONE).isAfter(moment.utc(event.startDate))
  ) {
    if (IS_CLIENT) {
      // on the client redirect to the 404 page
      Router.push("/404");
      return null;
    } else {
      // on the server render the Error page with a 404
      return <Error statusCode={404} />;
    }
  }

  const pageTitle = prismicGame?.meta_title || getPageTitle(slugId);
  const pageDescription =
    prismicGame?.meta_description ||
    `See who our recommended best bet is for ${event.awayTeam.fullName} vs. ${event.homeTeam.fullName}. ` +
      `Read our ${event.awayTeam.fullName} vs. ${event.homeTeam.fullName} prediction and find out which side ` +
      "the pros & public are betting on and more with BetQL.";

  const noIndexLeagues = [undefined, "UCL", "MLS", "DEB", "ITSA", "ESP", "ATP", "WTA"];
  const description = `${event.awayTeam.fullName} vs ${event.homeTeam.fullName} prediction`;

  let homeAbbrev: string = event.homeTeam.preferredAbbreviation ?? "";
  let awayAbbrev: string = event.awayTeam.preferredAbbreviation ?? "";
  if (currentSportObj.isTennis) {
    homeAbbrev = event.homeTeam.country ?? "";
    awayAbbrev = event.awayTeam.country ?? "";
  }

  return (
    <div className="game-detail">
      <NewsArticleSchema
        author="BetQL Staff"
        headline={description}
        pathname={`/${sportKey}/${slugId}`}
        datePublished={prismicGame?._meta?.firstPublicationDate || event?.startDate}
        dateModified={prismicGame?._meta?.lastPublicationDate || event?.startDate}
        image={"/ogimage.jpg"}
        description={description}
        articleBody={event?.analysis}
        articleSection={event?.league?.name || "BetQL Game Prediction"}
        keywords={description.split(" ")}
      />
      <LiveGameBar sport={currentSportObj} />
      <div className="game-detail__main">
        <NextSeo
          title={pageTitle}
          description={pageDescription}
          openGraph={{
            url: `https://www.betql.co/${sportKey}/${slugId}`,
            title: pageTitle,
            description: pageDescription,
            images: [
              {
                url: `/api/og?leagueName=${currentSportObj?.key}&awayTeam=${awayAbbrev}&homeTeam=${homeAbbrev}`,
                width: 600,
                height: 315,
                alt: pageTitle,
              },
            ],
          }}
          noindex={noIndexLeagues.includes(currentSportObj?.key)}
        />
        <GameDetailHeader
          game={event}
          currentSportObj={currentSportObj}
          lineFilter={lineFilter}
          prismicGame={prismicGame}
        />
        <Spacer size={16} />
        <div id="lines">
          <GameLines slugId={slugId} />
        </div>
        <Spacer size={16} />
        {!ctx.signedIn ? <EmailCapture style={{ marginBottom: 16 }} /> : null}
        <div className="game-detail__best-bets">
          <BestBets game={event} sport={currentSportObj} loading={eventResponse.loading} />
        </div>

        <Spacer size={16} />

        {currentSportObj.hasPlayerProps ? (
          <>
            <div id="props">
              <PlayerPropsPanel slugId={slugId} />
            </div>
            <Spacer size={16} />
          </>
        ) : null}
        <OffersCarousel />
        <Spacer size={16} />
        <RecommendedBets />
        <Spacer size={16} />
        <PopularEvents />
        <Spacer size={16} />
        <GameAnalysis event={event} currentSportObj={currentSportObj} prismicGame={prismicGame} />
        <Spacer size={16} />
        {currentSportObj.hasTeamSummary && (
          <>
            <TeamSummary slugId={slugId} currentSportObj={currentSportObj} />
            <Spacer size={16} />
          </>
        )}
        {currentSportObj.hasSchedule && !currentSportObj.isTennis && (
          <>
            <div id="schedule">
              <SchedulePanel event={event} />
            </div>
            <Spacer size={16} />
          </>
        )}
        {currentSportObj.hasTopTrendsData ? (
          <>
            <div id="trends">
              <TrendsOverView
                freeGame={event.freeGame}
                gameId={event.id}
                currentSportObj={currentSportObj}
                homeTeam={event.homeTeam}
                awayTeam={event.awayTeam}
              />
            </div>
            <Spacer size={16} />
          </>
        ) : null}
        <div id="movement">
          <Chart game={event} showData sportKey={sportKey} />
        </div>
        <Spacer size={16} />
        {currentSportObj.hasPitchers && (
          <>
            <div id="pitchers">
              <GameDetailStartingPitchers event={event} />
            </div>
            <Spacer size={16} />
          </>
        )}
        {currentSportObj.hasLineup ? (
          <>
            <div id="lineup">
              <MatchupTeamsPlayerLineup event={event} currentSportObj={currentSportObj} />
            </div>
            <Spacer size={16} />
          </>
        ) : null}
        {currentSportObj.hasInjuries ? (
          <>
            <div id="injury">
              <MatchupTeamsPlayerInjuries event={event} currentSportObj={currentSportObj} />
            </div>
            <Spacer size={16} />
          </>
        ) : null}
        <style jsx global>
          {styles}
        </style>
      </div>
    </div>
  );
};

export const getStaticPaths = async () => {
  const { data } = await initializeApollo().query({
    query: ALL_EVENTS_SLUGS_QUERY,
  });
  let slugs: EventSlugIdAndLeague[] = data?.slugs ? data.slugs : [];
  slugs = slugs.filter((slug) => slug.slugId);
  const paths = slugs.map((slug: EventSlugIdAndLeague) => ({
    params: { sport: SLUG_SPORTS_MAP[slug.league.name], slug: slug.slugId },
  }));
  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<GamesDetailViewProps> = async ({ params }) => {
  const slugId = getStringQueryParam(params?.slug);
  const sportKey = getStringQueryParam(params?.sport);
  const league = SPORTS_MAP[sportKey];
  if (!slugId || !league) {
    return {
      props: {
        staticEvent: null,
        prismicGame: null,
        slugId: "",
        sportKey: "",
      },
    };
  }
  currentSport(league);
  // Best for guaranteed SSR
  const result = await initializeApollo().query({
    query: EVENT_STATIC_QUERY,
    variables: {
      slugId: rfc3986EncodeURIComponent(decodeURIComponent(slugId)),
    },
  });

  return {
    props: {
      staticEvent: result?.data?.event ?? null,
      prismicGame: result?.data?.prismicGame ?? null,
      slugId,
      sportKey,
    },
    revalidate: 60 * 60, // every hour
  };
};

export default Game;
